import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner, Badge, InputGroup } from "react-bootstrap";
import { FaSearch, FaProjectDiagram, FaChevronLeft, FaChevronRight, FaCode } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import ProjectService from "../../services/projectService";
import "../../styles/projects.css"; // We'll add some inline styles if needed, or create this file

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ categories: [], languages: [] });
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  
  const navigate = useNavigate();
  const categoryScrollRef = useRef(null);

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      const scrollAmount = 300;
      categoryScrollRef.current.scrollBy({ 
        left: direction === 'left' ? -scrollAmount : scrollAmount, 
        behavior: 'smooth' 
      });
    }
  };

  useEffect(() => {
    loadFilters();
    loadProjects();
  }, [selectedCategory, selectedLanguage]);

  const loadFilters = async () => {
    try {
      const res = await ProjectService.getProjectFilters();
      setFilters(res.data.data || { categories: [], languages: [] });
    } catch (error) {
      console.error("Failed to load filters", error);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedLanguage) params.language = selectedLanguage;
      
      const res = await ProjectService.getAllProjects(params);
      setProjects(res.data?.data?.projects || []);
    } catch (error) {
      console.error("Failed to load projects", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFree = showFreeOnly ? parseFloat(p.price) === 0 : true;
    return matchesSearch && matchesFree;
  });

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      <PublicNavbar />
      
      {/* Hero Section */}
      <div className="projects-hero text-white mt-5" style={{ padding: '80px 0 100px 0' }}>
        <Container className="text-center position-relative" style={{ zIndex: 2 }}>
          <Badge bg="light" text="primary" className="mb-3 px-3 py-2 rounded-pill fw-bold" style={{ letterSpacing: '1px' }}>
            EXPLORE OUR COLLECTION
          </Badge>
          <h1 className="fw-bolder display-4 mb-3" style={{ letterSpacing: '-1px' }}>
            Source Code Marketplace
          </h1>
          <p className="lead mb-0 opacity-75 mx-auto" style={{ maxWidth: '600px' }}>
            Discover, learn, and deploy production-ready software projects tailored for your next big idea.
          </p>
        </Container>
      </div>

      <Container className="flex-grow-1 mb-5">
        
        {/* Top Filter Bar */}
        <div className="bg-white p-3 rounded-4 search-island mx-auto" style={{ maxWidth: '900px' }}>
          <Row className="g-3 align-items-center">
            <Col lg={5} md={12}>
              <InputGroup size="lg">
                <InputGroup.Text className="bg-white border-end-0 px-4 text-muted"><FaSearch /></InputGroup.Text>
                <Form.Control 
                  placeholder="Search by title or description..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0 ps-0 shadow-none"
                  style={{ backgroundColor: 'transparent' }}
                />
              </InputGroup>
            </Col>
            <Col lg={4} md={8}>
              <InputGroup size="lg">
                <InputGroup.Text className="bg-white border-end-0 text-muted"><FaCode /></InputGroup.Text>
                <Form.Select 
                  value={selectedLanguage} 
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="border-start-0 fw-semibold text-secondary shadow-none cursor-pointer"
                  style={{ backgroundColor: 'transparent' }}
                >
                  <option value="">All Tech Stacks</option>
                  {filters.languages && filters.languages.map(l => (
                    <option key={l.id} value={l.name}>{l.name}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
            <Col lg={3} md={4} className="d-flex align-items-center justify-content-lg-end mt-3 mt-md-0 px-3">
              <Form.Check 
                type="switch"
                id="free-projects-switch"
                label={<span className="fw-bold ms-1" style={{ color: showFreeOnly ? '#28a745' : '#6c757d' }}>Free Only</span>}
                checked={showFreeOnly}
                onChange={(e) => setShowFreeOnly(e.target.checked)}
                style={{ transform: 'scale(1.1)', margin: 0, cursor: 'pointer' }}
              />
            </Col>
          </Row>

          <hr className="my-3 opacity-10" />

          {/* Horizontal Scrolling Categories */}
          <div className="d-flex align-items-center">
            <Button 
              variant="light" 
              className="rounded-circle p-2 me-2 shadow-sm border"
              onClick={() => scrollCategories('left')}
            >
              <FaChevronLeft />
            </Button>
            
            <div 
              ref={categoryScrollRef}
              className="d-flex overflow-hidden py-1"
              style={{ whiteSpace: 'nowrap', scrollBehavior: 'smooth' }}
            >
              <button
                className={`btn rounded-pill px-4 fw-bold me-2 category-chip ${selectedCategory === "" ? "active" : ""}`}
                onClick={() => setSelectedCategory("")}
              >
                All Categories
              </button>
              {filters.categories && filters.categories.map(c => (
                <button
                  key={c.id}
                  className={`btn rounded-pill px-4 fw-bold me-2 category-chip ${selectedCategory === c.name ? "active" : ""}`}
                  onClick={() => setSelectedCategory(c.name)}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <Button 
              variant="light" 
              className="rounded-circle p-2 ms-2 shadow-sm border"
              onClick={() => scrollCategories('right')}
            >
              <FaChevronRight />
            </Button>
          </div>
        </div>

        {/* Project Grid */}
        {loading ? (
          <div className="text-center py-5 my-5">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <h5 className="mt-3 text-muted">Loading amazing projects...</h5>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-5 my-5 bg-white rounded-4 shadow-sm">
            <FaProjectDiagram size={60} className="text-muted mb-3 opacity-25" />
            <h4 className="fw-bold text-dark">No projects found.</h4>
            <p className="text-muted mb-0">Try adjusting your filters or search query to see more results.</p>
            <Button variant="outline-primary" className="mt-3 rounded-pill px-4" onClick={() => { setSelectedCategory(""); setSelectedLanguage(""); setSearchQuery(""); }}>
              Clear All Filters
            </Button>
          </div>
        ) : (
          <Row className="g-4">
            {filteredProjects.map((project) => (
              <Col md={6} lg={4} key={project.id}>
                <Card 
                  className="h-100 shadow-sm border-0 rounded-4 overflow-hidden project-card" 
                  style={{ transition: 'all 0.3s ease', cursor: 'pointer' }} 
                  onClick={() => navigate(`/projects/${project.id}`)}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.classList.add('shadow-lg'); }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.classList.remove('shadow-lg'); }}
                >
                  {/* Thumbnail Image */}
                  <div className="bg-light project-card-img-wrapper">
                    {parseFloat(project.price) === 0 && (
                      <div className="free-badge">FREE</div>
                    )}
                    {project.thumbnail_url ? (
                      <img src={project.thumbnail_url} alt={project.title} className="w-100 h-100 object-fit-cover" />
                    ) : project.images && project.images.length > 0 ? (
                      <img src={project.images[0]} alt={project.title} className="w-100 h-100 object-fit-cover" />
                    ) : (
                      <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-white">
                        <FaProjectDiagram size={60} className="text-primary opacity-25" />
                      </div>
                    )}
                    <div className="position-absolute bottom-0 start-0 w-100 p-3 d-flex flex-wrap gap-2" style={{ zIndex: 2 }}>
                      {project.categories && project.categories.slice(0, 2).map((cat, i) => (
                        <Badge className="rounded-pill px-3 py-2 glass-tag" key={i}>{cat}</Badge>
                      ))}
                      {project.languages && project.languages.slice(0, 2).map((lang, i) => (
                        <Badge bg="dark" className="rounded-pill px-3 py-2 border border-secondary shadow-sm" key={i}>{lang}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  <Card.Body className="d-flex flex-column p-4">
                    <Card.Title className="fw-bold mb-3 fs-5 text-dark lh-sm">{project.title}</Card.Title>
                    <Card.Text className="text-muted small flex-grow-1" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {project.description}
                    </Card.Text>
                    
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                      <div className="d-flex flex-column">
                        <span className="small text-muted fw-semibold">Price</span>
                        <span className="fw-bold text-success fs-5">{parseFloat(project.price) === 0 ? 'Free' : `₹${project.price}`}</span>
                      </div>
                      <Button variant="primary" className="rounded-pill px-4 fw-semibold shadow-sm" onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}>
                        View Details
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
      <Footer />
    </div>
  );
};

export default Projects;
